/**
 * WorldScene — abstract base for every explorable scene (owner: env-builder).
 * Implements IWorldScene (src/core/types/contracts.ts). Consumes only the index exports of
 * `@/maps`, `@/characters`, `@/effects`, `@/audio` — never their concrete files.
 *
 * Responsibilities: map runtime, player/follower/NPC spawning + control, integer-snapped camera
 * with pan/follow/shake, deterministic weather director + lighting overlay, feet-Y depth sorting,
 * a single fixed 1/60 simulation clock (drives the characters ActorSystem too), input lock,
 * position save hook and graceful degradation when maps/textures/actors are missing.
 *
 * Weather → NPC timing: NPC schedules switch to `weatherBehaviour[target]` when a transition STARTS
 * (t = 0), owned by `beginWeather` (sceneHelpers.ts); ambience and `onWeatherSettled` fire on t = 1.
 * Pure rules (spawn precedence, exit → scene key, fallback map, camera bounds) live in sceneHelpers.ts.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import { FixedStepper } from '@/core/clock';
import { ctxOf, type GameContext } from '@/core/context';
import { bus, Events } from '@/core/events';
import { SeededRng } from '@/core/rng';
import type { Facing, SceneEntryData, WeatherState } from '@/core/types';
import type { IActor, IEffectsDirector, IWorldScene, MapData, MapExit, MapInteraction } from '@/core/types/contracts';
import type { SceneKey } from '@/core/scenes/keys';
import { isStoryFlag } from '@/story/flags';
import { createMapRuntime, getMap, type MapRuntime } from '@/maps';
import { ActorSystem, Follower, NpcScheduleRunner as CharacterNpcRunner, PlayerController, createActor } from '@/characters';
import { InteractionSystem, storyGate, type InteractionGate } from '@/interaction';
import { createEffects } from '@/effects';
import { AmbiencePresets, AudioKeys, audio } from '@/audio';
import { WEATHER_LOOKS, WeatherDirector, isWeatherState, type WeatherLook } from './WeatherDirector';
import { NpcScheduleRunner } from './NpcScheduler';
import { DevFollowerTrail, DevPlayerMover } from './DevPlayerMover';
import { clampScroll, followScroll, panAt, shakeOffset, type Vec2 } from './cameraMath';
import { depthForFeet, type DepthSortable } from './depth';
import { facingFrom } from './movement';
import { beginWeather, cameraBoundsFor, fallbackMap, resolveSpawn, sceneKeyForExit, type SpawnPoint } from './sceneHelpers';

export interface WorldActorConfig { id: string; actor: string }
export interface WorldSceneConfig {
  player: WorldActorConfig;
  followers: WorldActorConfig[];
  /** Spawn used when entry data names none. Falls back to `start`, then the map's first spawn. */
  defaultSpawn?: string;
  /** Player walk speed override in px/s (default: the actor definition's speed). */
  playerSpeed?: number;
  /** Default weather transition length in ms (default 1500). */
  weatherTransitionMs?: number;
  /**
   * Ambience preset family from `@/audio` `AmbiencePresets` (e.g. `harbor` → `harbor-rain` when it rains).
   * Without it the map's `ambience` keys are layered with generic rain/wind beds from the weather look.
   */
  ambiencePreset?: string;
}

export const DEFAULT_WORLD_CONFIG: WorldSceneConfig = {
  player: { id: 'player', actor: 'jiang-zichao' },
  followers: [{ id: 'zhao', actor: 'zhao-chengbo' }],
  weatherTransitionMs: 1500,
};

/** The characters module's actor type, resolved through the factory so a class rename cannot break us. */
type CharacterActor = ReturnType<typeof createActor>;
function isCharacterActor(a: IActor): a is CharacterActor {
  const o = a as unknown as Record<string, unknown>;
  return typeof o['setVelocity'] === 'function' && typeof o['step'] === 'function' && typeof o['setPositionDriven'] === 'function';
}

/** Minimal actor used only when `createActor` throws, so a scene never dies on a broken asset. */
class FallbackActor implements IActor {
  readonly sprite: Phaser.GameObjects.Sprite;
  facing: Facing;
  constructor(scene: Phaser.Scene, readonly id: string, x: number, y: number, facing: Facing) {
    this.sprite = scene.add.sprite(Math.round(x), Math.round(y), '__MISSING').setOrigin(0.5, 1).setDisplaySize(32, 48).setTint(0xff4fd8);
    this.facing = facing;
    this.sprite.setDepth(depthForFeet(this.sprite.y));
  }
  get x(): number { return this.sprite.x; } set x(v: number) { this.sprite.x = Math.round(v); }
  get y(): number { return this.sprite.y; } set y(v: number) { this.sprite.y = Math.round(v); this.sprite.setDepth(depthForFeet(this.sprite.y)); }
  get displayName(): string { return this.id; }
  setFacing(f: Facing): void { this.facing = f; }
  play(): void { /* no animation */ }
  async walkTo(x: number, y: number): Promise<void> { this.x = x; this.y = y; }
  isMoving(): boolean { return false; }
  setPosition(x: number, y: number, facing?: Facing): void { this.x = x; this.y = y; if (facing) this.facing = facing; }
  stop(): void { /* nothing to stop */ }
  faceTowards(x: number, y: number): void { this.facing = facingFrom(x - this.x, y - this.y, this.facing); }
  async showEmote(): Promise<void> { /* none */ }
  setControllable(): void { /* none */ }
  destroy(): void { this.sprite.destroy(); }
}

interface PlayerDriver { setLocked(on: boolean): void; destroy(): void }
interface NpcDriver { setPaused(on: boolean): void; setWeather(s: WeatherState): void; destroy(): void }
interface PanState { from: Vec2; to: Vec2; elapsed: number; duration: number; resolve: () => void }
interface ShakeState { elapsed: number; duration: number; intensity: number }
type CameraMode = 'follow' | 'pan' | 'hold';

const POSITION_SAVE_MS = 2000;
const AMBIENCE_FADE_MS = 800;

/** Gate used when no GameContext is present (showcases / harness): everything enabled, marks kept locally. */
function permissiveGate(): InteractionGate {
  const marks = new Set<string>();
  return { hasFlag: () => true, hasMark: (m) => marks.has(m), setMark: (m) => { marks.add(m); } };
}

export type InteractKind = Exclude<MapInteraction['kind'], 'exit'>;

export abstract class WorldScene extends Phaser.Scene implements IWorldScene {
  /** Map id resolved through `getMap()` from `@/maps`. */
  protected abstract readonly mapId: string;
  /** Called after the world is built (map, actors, camera, weather, effects). */
  protected abstract onCreate(): void | Promise<void>;
  /** Called before the map/actors exist — e.g. to register maps or generate dev textures. */
  protected onPreCreate(): void { /* optional */ }
  /** Called at the start of shutdown, before the base class tears down. */
  protected onShutdown(): void { /* optional */ }
  /** Called once per fixed step after movement/schedules/weather ran. */
  protected onFixedStep(_dtMs: number): void { /* optional */ }
  /** Called every frame after depth sorting and camera placement. */
  protected onPostUpdate(): void { /* optional */ }
  /** Called when a weather transition settles (t = 1). */
  protected onWeatherSettled(_state: WeatherState): void { /* optional */ }
  /**
   * Called (edge-triggered) when the player steps into an exit rect, or confirms a `kind: 'exit'`
   * interaction (`via` set; `exit` null when the door matches no exit rect). Default: no-op — content
   * scenes route, typically with `this.goToExit(exit)`.
   */
  protected onExit(_exit: MapExit | null, _via?: MapInteraction): void { /* optional */ }
  /** Called when the player confirms a talk/observe/trigger interaction (or an `auto` one fires). Default: no-op; content routes to dialogue/cinematics. */
  protected onInteract(_kind: InteractKind, _interaction: MapInteraction): void { /* optional */ }
  protected worldConfig(): WorldSceneConfig { return DEFAULT_WORLD_CONFIG; }
  /**
   * Ambience layers for a settled weather state. Default: `AmbiencePresets[<preset>-<state>]` when the
   * config names a preset and the entry exists; else the map's `ambience` keys plus rain/wind beds
   * scaled by the weather look. Override for bespoke soundscapes.
   */
  protected ambienceFor(state: WeatherState): { key: string; volume: number }[] {
    const preset = this.worldConfig().ambiencePreset;
    if (preset) {
      const p = AmbiencePresets[`${preset}-${state}`];
      if (p) return p.map((l) => ({ ...l }));
    }
    const layers = (this._map?.ambience ?? []).map((key) => ({ key, volume: 1 }));
    const look = WEATHER_LOOKS[state];
    if (look.rain >= 0.7) layers.push({ key: AudioKeys.ambience.rainHeavy, volume: look.rain });
    else if (look.rain > 0) layers.push({ key: AudioKeys.ambience.rainLight, volume: look.rain });
    if (look.wind >= 0.4) layers.push({ key: AudioKeys.ambience.wind, volume: look.wind });
    return layers;
  }

  protected ctx: GameContext | null = null;
  protected entry: SceneEntryData = {};
  /** Layer for roofs/awnings/props drawn above every world object. */
  overhead!: Phaser.GameObjects.Layer;
  /** True when `createMapRuntime` added display objects (so no fallback rendering is needed). */
  protected mapRuntimeDrew = false;
  /** Total simulated time (ms) since create; advances only on fixed steps. */
  simTimeMs = 0;

  private _map!: MapData;
  private _runtime!: MapRuntime;
  private readonly _actors = new Map<string, IActor>();
  private _player!: IActor;
  private _followers: IActor[] = [];
  private stepper = new FixedStepper();
  private weatherDir = new WeatherDirector('sultry');
  private tintOverlay: Phaser.GameObjects.Rectangle | null = null;
  private hazeOverlay: Phaser.GameObjects.Rectangle | null = null;
  private effects: IEffectsDirector | null = null;
  private locked = false;
  private created = false;
  private playerDriver: PlayerDriver | null = null;
  private devMover: DevPlayerMover | null = null;
  private devTrail: DevFollowerTrail | null = null;
  private followerDrivers: { destroy(): void }[] = [];
  private npcDriver: NpcDriver | null = null;
  private readonly devRunners = new Map<string, NpcScheduleRunner>();
  private readonly worldObjects = new Map<DepthSortable, number>();
  private cameraMode: CameraMode = 'follow';
  private pan: PanState | null = null;
  private holdScroll: Vec2 = { x: 0, y: 0 };
  private shake: ShakeState | null = null;
  private shakeNow: Vec2 = { x: 0, y: 0 };
  private shakeRng = new SeededRng('camera-shake');
  private saveAcc = 0;
  private lastSaved: { x: number; y: number; facing: Facing } | null = null;
  private lastExitId: string | null = null;
  private interaction: InteractionSystem | null = null;
  private weatherHandlerRef: ((s: WeatherState) => void) | null = null;
  private readonly warnedTextures = new Set<string>();
  private readonly blockedFn = (px: number, py: number): boolean => this.isBlocked(px, py);
  private readonly fixedStepFn = (dt: number): void => this.fixedStep(dt);
  private readonly onConfirm = (): void => { this.interact(); };

  // ------------------------------------------------------------------ IWorldScene surface
  get map(): MapData { return this._map; }
  get mapRuntime(): MapRuntime { return this._runtime; }
  get actors(): Map<string, IActor> { return this._actors; }
  get player(): IActor { return this._player; }
  get followers(): IActor[] { return this._followers; }
  get weather(): WeatherState { return this.weatherDir.state; }
  set weather(v: WeatherState) { this.setWeather(v, 0); }
  get weatherLook(): WeatherLook { return this.weatherDir.look(); }
  get weatherProgress(): number { return this.weatherDir.progress; }
  get inputLocked(): boolean { return this.locked; }
  get isReady(): boolean { return this.created; }
  get stepCount(): number { return this.stepper.steps; }
  get mapPixelWidth(): number { return this._map.width * this._map.tileSize; }
  get mapPixelHeight(): number { return this._map.height * this._map.tileSize; }
  get cameraState(): { mode: CameraMode; shaking: boolean; panning: boolean } {
    return { mode: this.cameraMode, shaking: this.shake !== null, panning: this.pan !== null };
  }
  /** Which control path is active — `characters` (PlayerController) or `dev` (built-in mover fallback). */
  get controlMode(): 'characters' | 'dev' | 'none' { return this.devMover ? 'dev' : this.playerDriver ? 'characters' : 'none'; }
  /** The proximity/exit/prompt system over this map's interaction data (null when it failed to build). */
  get interactions(): InteractionSystem | null { return this.interaction; }
  /** Interaction a confirm press would use right now (drives the UI prompt). */
  get currentInteraction(): MapInteraction | null { return this.interaction?.current() ?? null; }

  // ------------------------------------------------------------------ lifecycle
  init(data: SceneEntryData): void {
    this.entry = data ?? {};
    this.created = false;
    this.locked = false;
    this.cameraMode = 'follow';
    this.pan = null;
    this.shake = null;
    this.shakeNow = { x: 0, y: 0 };
    this.saveAcc = 0;
    this.lastSaved = null;
    this.lastExitId = null;
    this.interaction = null;
    this.simTimeMs = 0;
    this.mapRuntimeDrew = false;
    this.stepper = new FixedStepper();
    this._actors.clear();
    this._followers = [];
    this.devRunners.clear();
    this.followerDrivers = [];
    this.worldObjects.clear();
  }

  create(): void {
    this.ctx = ctxOf(this) ?? null;
    const seed = this.ctx?.seed ?? 1157;
    this.shakeRng = new SeededRng(SeededRng.hash(`${seed}:shake:${this.mapId}`));
    const cfg = this.worldConfig();
    try { this.onPreCreate(); } catch (e) { console.warn('[world] onPreCreate failed', e); }

    // one simulation clock: the characters ActorSystem is stepped from our fixed loop
    try { ActorSystem.get(this).autoStep = false; } catch (e) { console.warn('[world] ActorSystem unavailable; actors step on their own clock', e); }

    this._map = this.loadMap();
    const cam = this.cameras.main;
    cam.setRoundPixels(true);
    // Phaser bounds would pin a map smaller than the view to the top-left; clampScroll centres those instead
    const view = this.viewSize();
    const bounds = cameraBoundsFor(this.mapPixelWidth, this.mapPixelHeight, view.w, view.h);
    if (bounds) cam.setBounds(bounds.x, bounds.y, bounds.w, bounds.h); else cam.removeBounds();
    cam.setDeadzone();
    this.overhead = this.add.layer().setDepth(Depth.OVERHEAD);

    const before = this.children.length;
    this._runtime = this.buildRuntime(this._map);
    this.mapRuntimeDrew = this.children.length > before;
    for (const layer of this._map.layers) if (!this.textures.exists(layer.tileset)) this.warnTexture(layer.tileset, `map layer ${layer.name}`);

    this.tintOverlay = this.add.rectangle(0, 0, GAME_W, GAME_H, 0xffffff, 1).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING).setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.hazeOverlay = this.add.rectangle(0, 0, GAME_W, GAME_H, 0xffffff, 0).setOrigin(0).setScrollFactor(0).setDepth(Depth.LIGHTING + 1).setBlendMode(Phaser.BlendModes.SCREEN);

    // weather state first: NPC runners read it at construction
    const initial = this.ctx?.weather ?? 'sultry';
    this.weatherDir = new WeatherDirector(isWeatherState(initial) ? initial : 'sultry');
    this.applyLook();

    // player + followers
    const spawn = this.resolveSpawn(cfg);
    this._player = this.spawnActor(cfg.player.id, cfg.player.actor, spawn.x, spawn.y, spawn.facing);
    this._player.setControllable(true);
    this.playerDriver = this.createPlayerDriver(this._player, cfg);
    this._followers = cfg.followers.map((f) => this.spawnActor(f.id, f.actor, spawn.x, spawn.y, spawn.facing));
    this.createFollowerDrivers(cfg);

    // NPCs
    this.npcDriver = this.createNpcDriver(seed);

    // interactions: prompts, auto triggers, exits (confirm arrives on the bus from InputMap / touch UI)
    this.interaction = this.createInteractionSystem();
    bus.onTyped(Events.INPUT_CONFIRM, this.onConfirm);

    if (this.ctx) {
      this.weatherHandlerRef = (s: WeatherState) => this.applyWeatherFromContext(s);
      this.ctx.weatherHandler = this.weatherHandlerRef;
    }

    // effects + audio
    try { this.effects = createEffects(this); this.effects.attach(this); } catch (e) { console.warn('[world] effects attach failed', e); this.effects = null; }
    try { audio().playMusic(this._map.music ?? null, AMBIENCE_FADE_MS); } catch (e) { console.warn('[world] music setup failed', e); }
    this.applyAmbience(this.weatherDir.state);

    this.events.on(Phaser.Scenes.Events.POST_UPDATE, this.postUpdate, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.updateCamera();
    this.sortDepths();
    this.created = true;
    bus.emitTyped(Events.WEATHER_CHANGE, { state: this.weatherDir.state, t: 1 });

    try {
      const r = this.onCreate();
      if (r instanceof Promise) r.catch((e: unknown) => console.warn('[world] onCreate rejected', e));
    } catch (e) { console.warn('[world] onCreate failed', e); }
  }

  override update(_time: number, delta: number): void {
    if (!this.created) return;
    this.stepper.tick(delta, this.fixedStepFn);
  }

  private fixedStep(dt: number): void {
    this.simTimeMs += dt;

    // weather
    const em = this.weatherDir.tick(dt);
    if (em || this.weatherDir.transitioning) this.applyLook();
    if (em) {
      bus.emitTyped(Events.WEATHER_CHANGE, em);
      if (em.t >= 1) this.settleWeather(em.state);
    }

    // simulation: characters module (controller → actors → followers → schedules), then dev fallbacks
    const p = this._player;
    const bx = p.x, by = p.y;
    try { ActorSystem.peek(this)?.step(dt); } catch (e) { console.warn('[world] actor system step failed', e); }
    if (this.devMover && !this.locked) this.devMover.tick(dt);
    this.devTrail?.tick(dt);
    for (const r of this.devRunners.values()) r.tick(dt);
    const moved = p.x !== bx || p.y !== by;

    // interactions (prompt / auto / exits) follow the feet position every step
    if (this.interaction) {
      try { this.interaction.update(p.x, p.y, p.facing); } catch (e) { console.warn('[world] interaction update failed', e); }
    } else if (moved && !this.locked) {
      // degraded path (interaction system unavailable): exits only, edge-triggered
      const exit = this._runtime.exitAt(p.x, p.y);
      const id = exit?.id ?? null;
      if (exit && id !== this.lastExitId) this.handleExit(exit);
      this.lastExitId = id;
    }

    // position save hook: every 2 s of sim time when the position changed since the last write
    this.saveAcc += dt;
    if (this.saveAcc >= POSITION_SAVE_MS) { this.saveAcc = 0; this.writePosition(); }

    // camera effects
    if (this.pan) {
      this.pan.elapsed += dt;
      if (this.pan.elapsed >= this.pan.duration) {
        const done = this.pan;
        this.pan = null;
        this.cameraMode = 'hold';
        this.holdScroll = done.to;
        done.resolve();
      }
    }
    if (this.shake) {
      this.shake.elapsed += dt;
      const remaining = 1 - this.shake.elapsed / this.shake.duration;
      if (remaining <= 0) { this.shake = null; this.shakeNow = { x: 0, y: 0 }; }
      else this.shakeNow = shakeOffset(() => this.shakeRng.next(), this.shake.intensity, remaining, GAME_W, GAME_H);
    }

    try { this.onFixedStep(dt); } catch (e) { console.warn('[world] onFixedStep failed', e); }
  }

  private postUpdate(): void {
    if (!this.created) return;
    this.sortDepths();
    this.updateCamera();
    try { this.onPostUpdate(); } catch (e) { console.warn('[world] onPostUpdate failed', e); }
  }

  private shutdown(): void {
    try { this.onShutdown(); } catch (e) { console.warn('[world] onShutdown failed', e); }
    this.events.off(Phaser.Scenes.Events.POST_UPDATE, this.postUpdate, this);
    bus.offTyped(Events.INPUT_CONFIRM, this.onConfirm);
    if (this.created) this.writePosition(true);
    this.created = false;
    this.interaction?.destroy(); this.interaction = null;
    for (const r of this.devRunners.values()) r.destroy();
    this.devRunners.clear();
    this.npcDriver?.destroy(); this.npcDriver = null;
    for (const d of this.followerDrivers) { try { d.destroy(); } catch { /* ignore */ } }
    this.followerDrivers = [];
    this.devTrail = null;
    this.playerDriver?.destroy(); this.playerDriver = null;
    this.devMover = null;
    if (this.pan) { const pending = this.pan; this.pan = null; pending.resolve(); }
    this.shake = null;
    for (const a of this._actors.values()) { try { a.destroy(); } catch { /* already gone */ } }
    this._actors.clear();
    this._followers = [];
    try { this._runtime?.destroy(); } catch { /* ignore */ }
    if (this.ctx && this.ctx.weatherHandler === this.weatherHandlerRef) this.ctx.weatherHandler = null;
    this.weatherHandlerRef = null;
    this.worldObjects.clear();
    this.tintOverlay = null;
    this.hazeOverlay = null;
    this.effects = null;
  }

  // ------------------------------------------------------------------ map / spawn / degradation
  private loadMap(): MapData {
    try {
      return getMap(this.mapId);
    } catch (e) {
      console.warn(`[world] map "${this.mapId}" missing (${String(e)}); running on an empty fallback map`);
      return WorldScene.fallbackMap(this.mapId);
    }
  }

  private buildRuntime(map: MapData): MapRuntime {
    try {
      return createMapRuntime(this, map);
    } catch (e) {
      console.warn(`[world] createMapRuntime failed for ${map.id}; retrying without visuals`, e);
      try {
        return createMapRuntime(this, map, { visuals: false, validate: false });
      } catch (e2) {
        console.warn(`[world] map ${map.id} unusable; falling back to an empty field`, e2);
        return createMapRuntime(this, WorldScene.fallbackMap(map.id), { visuals: false, validate: false });
      }
    }
  }

  /** 20 × 12 open field with a solid border, used when a map id is unknown; the scene keeps running. */
  static fallbackMap(id: string): MapData { return fallbackMap(id); }

  /** entry.position (same map) > entry.spawn > defaultSpawn > `start` > first spawn > map centre. */
  private resolveSpawn(cfg: WorldSceneConfig): SpawnPoint { return resolveSpawn(this.entry, this.mapId, this._map, cfg.defaultSpawn); }

  getSpawn(id: string): { x: number; y: number; facing: Facing } | null {
    const s = this._map.spawns.find((sp) => sp.id === id);
    return s ? { x: s.x, y: s.y, facing: s.facing } : null;
  }

  private warnTexture(key: string, where: string): void {
    if (this.warnedTextures.has(key)) return;
    this.warnedTextures.add(key);
    console.warn(`[world] missing texture "${key}" (${where}); placeholder in use`);
  }

  // ------------------------------------------------------------------ actors + drivers
  spawnActor(id: string, actorKey: string, x: number, y: number, facing: Facing): IActor {
    if (this._actors.has(id)) this.removeActor(id);
    let actor: IActor;
    try {
      actor = createActor(this, id, actorKey, Math.round(x), Math.round(y), facing, { isBlocked: this.blockedFn, bounds: { w: this.mapPixelWidth, h: this.mapPixelHeight } });
    } catch (e) {
      console.warn(`[world] createActor failed for ${id} (${actorKey}); fallback actor in use`, e);
      actor = new FallbackActor(this, id, x, y, facing);
    }
    this._actors.set(id, actor);
    actor.sprite.setDepth(depthForFeet(actor.y));
    return actor;
  }

  removeActor(id: string): boolean {
    const a = this._actors.get(id);
    if (!a) return false;
    this.devRunners.get(id)?.destroy();
    this.devRunners.delete(id);
    try { a.destroy(); } catch { /* ignore */ }
    this._actors.delete(id);
    this._followers = this._followers.filter((f) => f !== a);
    return true;
  }

  getActor(id: string): IActor | null { return this._actors.get(id) ?? null; }

  private createPlayerDriver(player: IActor, cfg: WorldSceneConfig): PlayerDriver {
    if (isCharacterActor(player)) {
      try {
        const opts: { mapId: string; speed?: number } = { mapId: this.mapId };
        if (cfg.playerSpeed !== undefined) opts.speed = cfg.playerSpeed;
        const pc = new PlayerController(this, player, opts);
        return { setLocked: (on) => pc.setInputLocked(on), destroy: () => pc.destroy() };
      } catch (e) { console.warn('[world] PlayerController failed; using the dev mover', e); }
    }
    const mover = new DevPlayerMover(this, player, this.blockedFn, { speed: cfg.playerSpeed ?? 80, mapId: this.mapId });
    this.devMover = mover;
    return { setLocked: (on) => mover.setEnabled(!on), destroy: () => { mover.destroy(); if (this.devMover === mover) this.devMover = null; } };
  }

  private createFollowerDrivers(cfg: WorldSceneConfig): void {
    const leader = this._player;
    const trailing: IActor[] = [];
    this._followers.forEach((f, i) => {
      if (isCharacterActor(f) && isCharacterActor(leader)) {
        try {
          const fo = new Follower(this, f, leader, { slot: i + 1 });
          this.followerDrivers.push({ destroy: () => fo.destroy() });
          return;
        } catch (e) { console.warn(`[world] Follower failed for ${f.id}; using the dev trail`, e); }
      }
      trailing.push(f);
    });
    if (trailing.length) {
      const trail = new DevFollowerTrail(leader, trailing, { spacingPx: 28, speed: (cfg.playerSpeed ?? 80) * 1.3 });
      this.devTrail = trail;
      this.followerDrivers.push({ destroy: () => { trail.destroy(); if (this.devTrail === trail) this.devTrail = null; } });
    }
  }

  private createNpcDriver(seed: number): NpcDriver | null {
    const placements = this._map.npcs;
    if (placements.length === 0) return null;
    const rng = new SeededRng(SeededRng.hash(`${seed}:npc:${this.mapId}`));
    let shared: InstanceType<typeof CharacterNpcRunner> | null = null;
    try { shared = new CharacterNpcRunner(this, rng, { initialWeather: this.weatherDir.state }); }
    catch (e) { console.warn('[world] characters NpcScheduleRunner failed; using the dev scheduler', e); }
    for (const npc of placements) {
      const a = this.spawnActor(npc.id, npc.actor, npc.x, npc.y, npc.facing);
      const hasSchedule = (npc.schedule?.length ?? 0) > 0 || !!npc.weatherBehaviour;
      if (!hasSchedule) continue;
      if (shared && isCharacterActor(a)) {
        try { shared.add(npc.id, a, npc.schedule ?? [], npc.weatherBehaviour); continue; }
        catch (e) { console.warn(`[world] schedule add failed for ${npc.id}; using the dev scheduler`, e); }
      }
      const r = new NpcScheduleRunner(a, npc.schedule ?? [], npc.weatherBehaviour ?? {});
      r.setWeather(this.weatherDir.state);
      this.devRunners.set(npc.id, r);
    }
    const runner = shared;
    return {
      setPaused: (on) => { runner?.setPaused(on); for (const r of this.devRunners.values()) r.setPaused(on); },
      setWeather: (s) => { runner?.setWeather(s); for (const r of this.devRunners.values()) r.setWeather(s); },
      destroy: () => { runner?.destroy(); },
    };
  }

  /** Register any positioned object so it is depth-sorted by feet Y each frame. */
  registerWorldObject(obj: DepthSortable, feetOffset = 0): void { this.worldObjects.set(obj, feetOffset); obj.setDepth(depthForFeet(obj.y + feetOffset)); }
  unregisterWorldObject(obj: DepthSortable): void { this.worldObjects.delete(obj); }

  private sortDepths(): void {
    for (const a of this._actors.values()) a.sprite.setDepth(depthForFeet(a.y));
    for (const [obj, off] of this.worldObjects) obj.setDepth(depthForFeet(obj.y + off));
  }

  // ------------------------------------------------------------------ collision / input lock / save
  isBlocked(px: number, py: number): boolean { return this._runtime.isBlocked(px, py); }

  setInputLocked(locked: boolean): void {
    if (this.locked === locked) return;
    this.locked = locked;
    this._player?.setControllable(!locked);
    this.playerDriver?.setLocked(locked);
    if (locked) { try { this._player?.stop(); } catch { /* ignore */ } }
    this.npcDriver?.setPaused(locked);
    this.interaction?.setLocked(locked);
  }

  /** Writes the player's position into the shared context (autosave picks it up). Skips unchanged positions unless `force`. */
  writePosition(force = false): boolean {
    if (!this.ctx || !this._player) return false;
    const p = this._player;
    const same = this.lastSaved && this.lastSaved.x === p.x && this.lastSaved.y === p.y && this.lastSaved.facing === p.facing;
    if (same && !force) return false;
    this.lastSaved = { x: p.x, y: p.y, facing: p.facing };
    this.ctx.position = { map: this.mapId, x: p.x, y: p.y, facing: p.facing };
    return true;
  }

  // ------------------------------------------------------------------ interactions / exits
  private createInteractionSystem(): InteractionSystem | null {
    try {
      const gate = this.ctx ? storyGate(this.ctx.story, isStoryFlag) : permissiveGate();
      const sys = new InteractionSystem({ interactions: this._map.interactions, exits: this._map.exits }, gate, {
        onTalk: (i) => this.handleInteract('talk', i),
        onObserve: (i) => this.handleInteract('observe', i),
        onTrigger: (i) => this.handleInteract('trigger', i),
        onExit: (exit, via) => this.handleExit(exit, via),
      });
      return sys;
    } catch (e) {
      console.warn('[world] InteractionSystem unavailable; exits only', e);
      return null;
    }
  }

  private handleInteract(kind: InteractKind, i: MapInteraction): void {
    try { this.onInteract(kind, i); } catch (e) { console.warn(`[world] onInteract(${kind}, ${i.id}) failed`, e); }
  }

  private handleExit(exit: MapExit | null, via?: MapInteraction): void {
    if (exit) this.writePosition(true);
    try { this.onExit(exit, via); } catch (e) { console.warn('[world] onExit failed', e); }
  }

  /**
   * Confirm press: resolves the current interaction through `onInteract` / `onExit`. Public so touch
   * buttons and tests can trigger it; returns the interaction used (null when none / locked).
   */
  interact(): MapInteraction | null {
    if (!this.created || this.locked || !this.interaction) return null;
    return this.interaction.tryInteract();
  }

  /**
   * Maps an exit's `target` to a scene key: an exact `SceneKeys` value, or a map id such as
   * `green-harbor` → `GreenHarbor`. Null when nothing matches (content should warn and stay put).
   */
  static sceneKeyForExit(target: string): SceneKey | null { return sceneKeyForExit(target); }

  /** Routes through the exit with the shared Router (fade, stop, start). Resolves false when no route exists. */
  protected async goToExit(exit: MapExit | null): Promise<boolean> {
    if (!exit || !this.ctx) return false;
    const key = WorldScene.sceneKeyForExit(exit.target);
    if (!key) { console.warn(`[world] exit "${exit.id}" targets unknown scene "${exit.target}"`); return false; }
    if (this.ctx.router.isBusy()) return false;
    this.setInputLocked(true);
    this.writePosition(true);
    await this.ctx.router.go(key, { spawn: exit.spawn });
    return true;
  }

  // ------------------------------------------------------------------ weather
  setWeather(state: WeatherState, transitionMs?: number): void {
    if (!isWeatherState(state)) { console.warn(`[world] unknown weather "${String(state)}" ignored`); return; }
    const ms = transitionMs ?? this.worldConfig().weatherTransitionMs ?? 1500;
    // NPC behaviour follows the target state from the start of the transition (before the bus hears it)
    const em = beginWeather(this.weatherDir, this.npcDriver, state, ms);
    if (this.ctx) this.ctx.weather = state;
    this.applyLook();
    if (em) {
      bus.emitTyped(Events.WEATHER_CHANGE, em);
      if (em.t >= 1) this.settleWeather(state);
    }
  }

  /** Path used by `ctx.setWeather` (debug API / save restore): instant; ctx emits t=1 itself. */
  private applyWeatherFromContext(state: WeatherState): void {
    if (!isWeatherState(state)) return;
    beginWeather(this.weatherDir, this.npcDriver, state, 0);
    this.applyLook();
    this.settleWeather(state);
  }

  /** t = 1: ambience and the content hook (NPCs already switched at t = 0 in `beginWeather`). */
  private settleWeather(state: WeatherState): void {
    this.applyAmbience(state);
    try { this.onWeatherSettled(state); } catch (e) { console.warn('[world] onWeatherSettled failed', e); }
  }

  private applyAmbience(state: WeatherState): void {
    try { audio().setAmbience(this.ambienceFor(state), AMBIENCE_FADE_MS); } catch (e) { console.warn('[world] ambience switch failed', e); }
  }

  private applyLook(): void {
    const l = this.weatherDir.look();
    this.tintOverlay?.setFillStyle(l.tint, l.tintAlpha);
    this.hazeOverlay?.setFillStyle(l.haze, l.hazeAlpha);
  }

  // ------------------------------------------------------------------ camera
  private viewSize(): { w: number; h: number } {
    const cam = this.cameras.main;
    return { w: Math.round(cam.width / (cam.zoomX || 1)), h: Math.round(cam.height / (cam.zoomY || 1)) };
  }

  private baseScroll(): Vec2 {
    const { w, h } = this.viewSize();
    const ww = this.mapPixelWidth, wh = this.mapPixelHeight;
    if (this.cameraMode === 'pan' && this.pan) {
      const p = this.pan;
      return panAt(p.from, p.to, p.duration <= 0 ? 1 : p.elapsed / p.duration);
    }
    if (this.cameraMode === 'hold') return clampScroll(this.holdScroll.x, this.holdScroll.y, w, h, ww, wh);
    return followScroll(this._player.x, this._player.y - 16, w, h, ww, wh);
  }

  private updateCamera(): void {
    const base = this.baseScroll();
    this.cameras.main.setScroll(base.x + this.shakeNow.x, base.y + this.shakeNow.y);
  }

  cameraPanTo(x: number, y: number, ms: number): Promise<void> {
    const { w, h } = this.viewSize();
    const to = clampScroll(Math.round(x - w / 2), Math.round(y - h / 2), w, h, this.mapPixelWidth, this.mapPixelHeight);
    if (this.pan) { const old = this.pan; this.pan = null; old.resolve(); }
    const cam = this.cameras.main;
    const from = { x: Math.round(cam.scrollX - this.shakeNow.x), y: Math.round(cam.scrollY - this.shakeNow.y) };
    if (ms <= 0) {
      this.cameraMode = 'hold';
      this.holdScroll = to;
      this.updateCamera();
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.pan = { from, to, elapsed: 0, duration: ms, resolve };
      this.cameraMode = 'pan';
    });
  }

  cameraFollowPlayer(): void {
    if (this.pan) { const old = this.pan; this.pan = null; old.resolve(); }
    this.cameraMode = 'follow';
  }

  /** Deterministic shake (SeededRng). `intensity` < 1 = fraction of the viewport, ≥ 1 = pixels. */
  cameraShake(ms: number, intensity: number): void {
    if (ms <= 0 || intensity <= 0) return;
    this.shake = { elapsed: 0, duration: ms, intensity };
  }
}
